import type { MasonryEngineActionHandler } from "@leagueplatform/masonry-engine";
import type { SchedulingFlowInitialData } from "@leagueplatform/care-finder";
import { awaitSchedulingFlowOpenOverlayHandle } from "utils/scheduling-flow/scheduling-flow-bridge";

// QUEST-2796 — mirrors the Android sequence: wait for the bridge to be
// mounted (so we never orphan a backend session), POST the DP with the
// tile's initialData, take sessionId from the response, then open the
// overlay so the shell resumes the just-created session. Modeled on the
// SDK's `startReschedulingFlowActionHandler`; drop this override once the
// SDK's default handler adopts the same POST-first pattern.

const DATA_PROCESSOR_ID = "startSchedulingFlow";
const SESSION_ID_KEY = "sessionId";

type StartSchedulingFlowPayload = {
  initialData?: SchedulingFlowInitialData;
};

const isFieldEntry = (f: unknown): f is { id: string; value: unknown } =>
  typeof f === "object" &&
  f !== null &&
  "id" in f &&
  typeof (f as { id: unknown }).id === "string";

export const QuestStartSchedulingFlowActionHandler: MasonryEngineActionHandler<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
> = async (action, resources) => {
  const payload = (action.payload ?? {}) as StartSchedulingFlowPayload;
  const initialData = payload.initialData ?? [];

  // Wait for the bridge before touching the DP so a bridge-never-mounts
  // failure short-circuits without creating an orphaned backend session.
  let openOverlay: (data?: SchedulingFlowInitialData) => void;
  try {
    openOverlay = await awaitSchedulingFlowOpenOverlayHandle();
  } catch (bridgeError) {
    return {
      isSuccess: false,
      error:
        bridgeError instanceof Error
          ? bridgeError
          : new Error(String(bridgeError)),
    };
  }

  const result = await resources.invokeAction({
    type: "invokeDataProcessor",
    namespace: "default",
    payload: {
      dataProcessorId: DATA_PROCESSOR_ID,
      fields: initialData.map(({ key, value }) => ({ id: key, value })),
      suppressErrorModal: false,
    },
  });

  if (!result) {
    return {
      isSuccess: false,
      error: new Error("startSchedulingFlow data processor returned no result"),
    };
  }
  if (!result.isSuccess) {
    return { isSuccess: false, error: result.error };
  }

  const responseData = result.data as { fields?: unknown } | undefined;
  const responseFields = responseData?.fields;
  if (!Array.isArray(responseFields)) {
    return {
      isSuccess: false,
      error: new Error(
        "startSchedulingFlow data processor returned unexpected response shape"
      ),
    };
  }

  const fieldMap = new Map<string, unknown>(
    responseFields.filter(isFieldEntry).map((f) => [f.id, f.value] as const)
  );
  const sessionId = fieldMap.get(SESSION_ID_KEY);
  if (typeof sessionId !== "string" || sessionId.length === 0) {
    return {
      isSuccess: false,
      error: new Error(
        "startSchedulingFlow data processor returned no sessionId"
      ),
    };
  }

  const dedupedInitialData = initialData.filter(
    ({ key }) => key !== SESSION_ID_KEY
  );
  openOverlay([
    ...dedupedInitialData,
    { key: SESSION_ID_KEY, value: sessionId },
  ]);
  return { isSuccess: true };
};
